import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FilterFilled } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { txt } from 'libs/i18n';
import SpeakerAvatar from 'components/SpeakerAvatarField/SpeakerAvatar';
import { useSession } from 'components/Authenticated';
import ColumnHeader from 'components/ColumnHeader';
import DateRangeFilter from 'components/DateRangeFilter';
import SpeakerRowActions from './SpeakerRowActions';
import SpeakerNameText from './SpeakerNameText';
import { RolesCell } from './RolesCell';
import { SpeakerIdActiveIcon } from './SpeakerIdActiveIcon';
import styles from './style.module.less';
import { renderDate } from '../../ProjectsPage/ProjectPanel';
export const useColumns = (onOpenModal, tableView, isLoaded, onRefetch, languages) => {
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const { session } = useSession();
    const isSpeakerIdEnabled = session.login.hasClaim('speakerId:enabled');
    const isOneLanguage = languages !== undefined && languages.length === 1;
    const { sieve } = tableView.getViewParams();
    const { sortBy, sortOrder, filters: sieveFilters, search: searchQuery, updatedDates = [], } = sieve !== null && sieve !== void 0 ? sieve : {};
    const onSort = (columnKey, order) => {
        tableView.setSieve(Object.assign(Object.assign({}, sieve), { filterDates: [], sortBy: columnKey, sortOrder: order }));
    };
    const handleFilterDatesChanged = (dates) => {
        tableView.setSieve(Object.assign(Object.assign({}, sieve), { filterDates: [], updatedDates: dates }));
    };
    const handleClearDateFilter = () => {
        tableView.setSieve(Object.assign(Object.assign({}, sieve), { filterDates: [], updatedDates: [] }));
    };
    return (useMemo(() => {
        var _a;
        return [
            {
                title: (_jsx(ColumnHeader, { title: "name", columnKey: "fullName", isSorting: sortBy === 'fullName', sortOrder: sortOrder, onSort: onSort })),
                dataIndex: 'fullName',
                width: '340px',
                render: (_, record) => {
                    var _a;
                    const displayAvatar = record.rowType === 'speaker' || (record.rowType === 'localization' && isOneLanguage);
                    const hasIcon = record.children !== undefined && record.children.length > 0;
                    return (_jsxs("div", { className: clsx(styles.nameCell, { [styles.nameCellWithIcon]: hasIcon }), children: [displayAvatar && (_jsx(SpeakerAvatar, { value: record.imageBase64, size: "small" })), _jsx(SpeakerNameText, { locale: record.locale, name: `${(_a = record.firstName) !== null && _a !== void 0 ? _a : ''} ${record.lastName}`, variantCount: record.variantCount, isDefault: record.isDefault })] }));
                },
            },
            {
                title: txt('roles'),
                dataIndex: 'roles',
                width: '220px',
                render: (_, record) => (_jsx(RolesCell, { rowType: record.rowType, roles: record.roles, defaultRoleIndex: record.defaultRoleIndex })),
            },
            {
                title: (_jsx(ColumnHeader, { title: "updated", columnKey: "lastUpdated", isSorting: sortBy === 'lastUpdated', sortOrder: sortOrder, onSort: onSort })),
                dataIndex: 'lastEdited',
                filteredValue: updatedDates.length !== 0 ? updatedDates.map(String) : null,
                filterDropdownOpen,
                onFilterDropdownOpenChange: setFilterDropdownOpen,
                filterIcon: (_jsx(FilterFilled, { className: clsx({ [styles.filterActive]: updatedDates.length !== 0 }) })),
                filterDropdown: (_jsx("div", { className: styles.filterDialog, children: _jsx(DateRangeFilter, { filterDates: updatedDates, onFilterDatesChanged: handleFilterDatesChanged, onClearFilter: handleClearDateFilter, onDropdownVisibility: setFilterDropdownOpen }) })),
                render: (_, record) => {
                    if (record.rowType === 'variant' || (record.rowType === 'localization' && !isOneLanguage)) {
                        return null;
                    }
                    return renderDate(record.lastEdited);
                },
                align: 'center',
                width: '160px',
            },
            ...(isSpeakerIdEnabled ? [{
                    title: txt('autodetection'),
                    key: 'speakerIdActive',
                    dataIndex: 'speakerIdActive',
                    align: 'center',
                    width: '140px',
                    filterMultiple: false,
                    filters: [
                        { text: txt('active'), value: true },
                        { text: txt('notActive'), value: false },
                    ],
                    filteredValue: (_a = sieveFilters === null || sieveFilters === void 0 ? void 0 : sieveFilters.speakerIdActive) !== null && _a !== void 0 ? _a : null,
                    render: (_, record) => (_jsx(SpeakerIdActiveIcon, { active: record.speakerIdActive })),
                }] : []),
            {
                title: txt('language'),
                dataIndex: 'language',
                width: '120px',
                render: (_, record) => {
                    var _a, _b, _c;
                    if (record.locale !== null) {
                        return record.locale.lang;
                    }
                    const defaultChild = (_a = record.children) === null || _a === void 0 ? void 0 : _a.find((child) => child.isDefault);
                    return (_c = (_b = defaultChild === null || defaultChild === void 0 ? void 0 : defaultChild.locale) === null || _b === void 0 ? void 0 : _b.lang) !== null && _c !== void 0 ? _c : '';
                },
            },
            {
                title: '',
                dataIndex: 'actions',
                fixed: 'right',
                width: '95px',
                render: (_, record) => (_jsx(SpeakerRowActions, { record: record, languages: languages, onOpenModal: onOpenModal, onRefetch: onRefetch })),
            },
        ];
    }, [
        isSpeakerIdEnabled, isLoaded, searchQuery, onOpenModal, onRefetch, languages,
        sortBy, sortOrder, onSort, tableView, sieve, sieveFilters,
    ]));
};
